import { useState } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  ChevronRight, ChevronLeft, Sparkles, Loader2, Building2,
  Target, Clipboard, Flag, Download, Send, CheckCircle,
  FileText, Clock, Users, Shield, ArrowRight
} from "lucide-react";

const STEPS = [
  { id: "client", label: "Client Info", icon: Building2 },
  { id: "sector", label: "Sector", icon: Shield },
  { id: "scope", label: "Scope", icon: Clipboard },
  { id: "objectives", label: "Objectives", icon: Target },
  { id: "goals", label: "Goals", icon: Flag },
];

const SECTORS = [
  { id: "life-sciences", label: "Life Sciences / Pharma", standards: ["ISO 9001", "FDA 21 CFR 210/211", "ICH Q7", "ICH Q10", "EU GMP Annex 1", "ISO 13485"] },
  { id: "medical-devices", label: "Medical Devices", standards: ["ISO 13485", "FDA 21 CFR 820", "EU MDR 2017/745", "ISO 14971", "IEC 62304"] },
  { id: "automotive", label: "Automotive", standards: ["IATF 16949", "VDA 6.3", "ISO 9001", "AIAG Core Tools", "CQI Standards"] },
  { id: "aerospace", label: "Aerospace & Defense", standards: ["AS9100 Rev D", "AS9102", "NADCAP", "DO-178C", "ISO 9001"] },
  { id: "industrial", label: "Industrial Manufacturing", standards: ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 50001"] },
  { id: "iot", label: "IoT / Technology", standards: ["ISO 27001", "ISO 9001", "IEC 62443", "NIST Cybersecurity"] },
];

const OBJECTIVE_OPTIONS = [
  "Achieve new certification/registration",
  "Maintain/upgrade existing certification",
  "Regulatory inspection readiness",
  "Improve process capability & reduce defects",
  "Implement CAPA system improvements",
  "Supplier quality program development",
  "Training program design & execution",
  "Document control system overhaul",
  "Risk management system implementation",
  "Data integrity remediation",
  "Validation/qualification services",
  "Internal audit program improvement",
];

const ClientQuestionnairePage = () => {
  const { t } = useTranslation('modules');
  const [step, setStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [form, setForm] = useState({
    client_name: "",
    company: "",
    email: "",
    phone: "",
    sector: "",
    sub_sectors: [],
    scope: "",
    objectives: [],
    goals: "",
    current_certifications: [],
    timeline_preference: "",
    budget_range: "",
    additional_notes: "",
  });

  const updateForm = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const toggleArray = (key, val) => {
    setForm((p) => ({
      ...p,
      [key]: p[key].includes(val) ? p[key].filter((v) => v !== val) : [...p[key], val],
    }));
  };

  const canNext = () => {
    switch (step) {
      case 0: return form.client_name && form.company;
      case 1: return form.sector;
      case 2: return form.scope;
      case 3: return form.objectives.length > 0;
      case 4: return form.goals;
      default: return false;
    }
  };

  const generateProposal = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/proposals/generate`, form, { withCredentials: true });
      setProposal(res.data);
    } catch (err) {
      console.error("Failed to generate proposal", err);
    } finally {
      setGenerating(false);
    }
  };

  const exportProposal = () => {
    if (!proposal?.proposal) return;
    const p = proposal.proposal;
    let md = `# ${p.title || "Project Proposal"}\n\n`;
    md += `**Prepared for:** ${form.company} (${form.client_name})\n`;
    md += `**Date:** ${new Date().toLocaleDateString()}\n`;
    md += `**Proposal ID:** ${proposal.proposal_id}\n\n`;
    md += `---\n\n## Executive Summary\n\n${p.executive_summary || ""}\n\n`;
    if (p.scope_of_work) {
      md += `## Scope of Work\n\n`;
      p.scope_of_work.forEach((ph) => {
        md += `### ${ph.phase}: ${ph.title}\n`;
        md += `${ph.description}\n`;
        md += `**Duration:** ${ph.duration}\n\n`;
        md += `**Deliverables:**\n`;
        (ph.deliverables || []).forEach((d) => (md += `- ${d}\n`));
        md += `\n`;
      });
    }
    if (p.methodology) md += `## Methodology\n\n${p.methodology}\n\n`;
    if (p.team_composition) {
      md += `## Team Composition\n\n`;
      p.team_composition.forEach((t) => (md += `- **${t.role}:** ${t.responsibility}\n`));
      md += `\n`;
    }
    if (p.timeline) md += `## Timeline\n\n**Total Duration:** ${p.timeline.total_duration}\n\n`;
    if (p.success_criteria) {
      md += `## Success Criteria\n\n`;
      p.success_criteria.forEach((c) => (md += `- ${c}\n`));
      md += `\n`;
    }
    if (p.risks_and_mitigations) {
      md += `## Risks & Mitigations\n\n`;
      p.risks_and_mitigations.forEach((r) => (md += `- **${r.risk}:** ${r.mitigation}\n`));
      md += `\n`;
    }
    if (p.next_steps) {
      md += `## Next Steps\n\n`;
      p.next_steps.forEach((s, i) => (md += `${i + 1}. ${s}\n`));
      md += `\n`;
    }
    if (p.why_charstan) md += `## Why Charstan\n\n${p.why_charstan}\n`;

    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${form.company.replace(/\s+/g, "_")}_Proposal_${proposal.proposal_id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedSector = SECTORS.find((s) => s.id === form.sector);

  // ============ PROPOSAL VIEW ============
  if (proposal) {
    const p = proposal.proposal;
    return (
      <QMSLayout title={t('modules:clientQuestionnaire.title')}>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
            <div>
              <span className="badge badge-cyan text-xs mb-2 inline-block">{proposal.proposal_id}</span>
              <h2 className="font-['Syne'] text-2xl font-bold text-white">{p.title || "Project Proposal"}</h2>
              <p className="text-[#8B95A5] text-sm mt-1">
                Prepared for <span className="text-[#CBD5E1]">{form.company}</span> ({form.client_name})
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setProposal(null)} className="btn-ghost py-2 px-3 text-xs" data-testid="new-proposal-btn">
                New Questionnaire
              </button>
              <button onClick={exportProposal} className="btn-primary py-2 px-3 text-xs" data-testid="export-proposal-btn">
                <Download size={14} /> Export
              </button>
            </div>
          </div>

          {/* Executive Summary */}
          {p.executive_summary && (
            <div className="qms-card mb-6">
              <div className="qms-card-body">
                <h3 className="font-['Syne'] text-base font-semibold text-white mb-3">Executive Summary</h3>
                <p className="text-[#CBD5E1] text-sm leading-relaxed whitespace-pre-wrap">{p.executive_summary}</p>
              </div>
            </div>
          )}

          {/* Scope of Work */}
          {p.scope_of_work && (
            <div className="mb-6">
              <h3 className="font-['Syne'] text-base font-semibold text-white mb-3">Scope of Work</h3>
              <div className="space-y-3">
                {p.scope_of_work.map((phase, i) => (
                  <div key={i} className="qms-card">
                    <div className="qms-card-body">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="w-8 h-8 rounded-full bg-[#36A9E1] bg-opacity-15 flex items-center justify-center text-[#38BDF8] text-sm font-bold flex-shrink-0">
                          {i + 1}
                        </span>
                        <div>
                          <h4 className="text-white text-sm font-semibold">{phase.title}</h4>
                          <span className="text-[#8B95A5] text-xs flex items-center gap-1">
                            <Clock size={10} /> {phase.duration}
                          </span>
                        </div>
                      </div>
                      <p className="text-[#CBD5E1] text-sm mb-3 ml-11">{phase.description}</p>
                      {phase.deliverables && (
                        <div className="ml-11">
                          <span className="text-[#8B95A5] text-xs font-medium">Deliverables:</span>
                          <ul className="mt-1 space-y-1">
                            {phase.deliverables.map((d, j) => (
                              <li key={j} className="flex items-start gap-2 text-[#CBD5E1] text-xs">
                                <CheckCircle size={12} className="text-[#4ADE80] flex-shrink-0 mt-0.5" />
                                {d}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Team & Timeline side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {p.team_composition && (
              <div className="qms-card">
                <div className="qms-card-body">
                  <h3 className="font-['Syne'] text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Users size={14} className="text-[#E8A838]" /> Team
                  </h3>
                  <div className="space-y-2">
                    {p.team_composition.map((t, i) => (
                      <div key={i}>
                        <span className="text-[#E8A838] text-xs font-medium">{t.role}</span>
                        <p className="text-[#CBD5E1] text-xs">{t.responsibility}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {p.timeline && (
              <div className="qms-card">
                <div className="qms-card-body">
                  <h3 className="font-['Syne'] text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Clock size={14} className="text-[#36A9E1]" /> Timeline
                  </h3>
                  <p className="text-[#CBD5E1] text-sm mb-3">Total: <span className="text-white font-medium">{p.timeline.total_duration}</span></p>
                  {p.timeline.phases && (
                    <div className="space-y-1.5">
                      {p.timeline.phases.map((ph, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="text-[#CBD5E1]">{ph.name}</span>
                          <span className="text-[#8B95A5]">{ph.weeks}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Success Criteria */}
          {p.success_criteria && (
            <div className="qms-card mb-6">
              <div className="qms-card-body">
                <h3 className="font-['Syne'] text-sm font-semibold text-white mb-3">Success Criteria</h3>
                <ul className="space-y-1.5">
                  {p.success_criteria.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#CBD5E1] text-sm">
                      <Target size={14} className="text-[#4ADE80] flex-shrink-0 mt-0.5" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Risks */}
          {p.risks_and_mitigations && (
            <div className="qms-card mb-6">
              <div className="qms-card-body">
                <h3 className="font-['Syne'] text-sm font-semibold text-white mb-3">Risks & Mitigations</h3>
                <div className="space-y-2">
                  {p.risks_and_mitigations.map((r, i) => (
                    <div key={i} className="bg-[#181B23] p-3 rounded-[3px]">
                      <span className="text-[#F87171] text-xs font-medium">{r.risk}</span>
                      <p className="text-[#CBD5E1] text-xs mt-0.5">{r.mitigation}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Next Steps */}
          {p.next_steps && (
            <div className="qms-card mb-6">
              <div className="qms-card-body">
                <h3 className="font-['Syne'] text-sm font-semibold text-white mb-3">Next Steps</h3>
                <ol className="space-y-1.5">
                  {p.next_steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#CBD5E1] text-sm">
                      <span className="w-5 h-5 rounded-full bg-[#E8A838] bg-opacity-15 flex items-center justify-center text-[#E8A838] text-xs font-bold flex-shrink-0">
                        {i + 1}
                      </span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* Why Charstan */}
          {p.why_charstan && (
            <div className="qms-card mb-6 border-[#E8A838] border-opacity-20">
              <div className="qms-card-body">
                <h3 className="font-['Syne'] text-sm font-semibold text-[#E8A838] mb-2">Why Charstan</h3>
                <p className="text-[#CBD5E1] text-sm leading-relaxed">{p.why_charstan}</p>
              </div>
            </div>
          )}
        </div>
      </QMSLayout>
    );
  }

  // ============ GENERATING VIEW ============
  if (generating) {
    return (
      <QMSLayout title={t('modules:clientQuestionnaire.title')}>
        <div className="flex flex-col items-center justify-center py-24">
          <Loader2 size={48} className="text-[#E8A838] animate-spin mb-6" />
          <h3 className="font-['Syne'] text-xl font-bold text-white mb-2">Generating Project Proposal</h3>
          <p className="text-[#8B95A5] text-center max-w-md">
            CHARSTAN Principal Engineer is analyzing your requirements and preparing a tailored proposal for {form.company}...
          </p>
        </div>
      </QMSLayout>
    );
  }

  // ============ QUESTIONNAIRE FORM ============
  return (
    <QMSLayout title={t('modules:clientQuestionnaire.title')}>
      <div className="max-w-3xl mx-auto">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8" data-testid="questionnaire-steps">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center">
              <button
                onClick={() => i <= step && setStep(i)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all ${
                  i === step
                    ? "bg-[#E8A838] bg-opacity-15 text-[#E8A838] border border-[#E8A838]"
                    : i < step
                    ? "bg-[#4ADE80] bg-opacity-10 text-[#4ADE80] border border-[#4ADE80] border-opacity-30"
                    : "bg-[#1F222D] text-[#8B95A5] border border-[rgba(255,255,255,0.06)]"
                }`}
              >
                {i < step ? <CheckCircle size={12} /> : <s.icon size={12} />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <div className={`w-8 h-px mx-1 ${i < step ? "bg-[#4ADE80]" : "bg-[#2D313D]"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="qms-card">
          <div className="qms-card-body space-y-5">
            {/* Step 0: Client Info */}
            {step === 0 && (
              <>
                <h3 className="font-['Syne'] text-lg font-bold text-white">Client Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">Contact Name *</label>
                    <input type="text" value={form.client_name} onChange={(e) => updateForm("client_name", e.target.value)} className="qms-input" placeholder="e.g., John Smith" data-testid="q-client-name" />
                  </div>
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">Company *</label>
                    <input type="text" value={form.company} onChange={(e) => updateForm("company", e.target.value)} className="qms-input" placeholder="e.g., PharmaCorp Ltd" data-testid="q-company" />
                  </div>
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">Email</label>
                    <input type="email" value={form.email} onChange={(e) => updateForm("email", e.target.value)} className="qms-input" placeholder="john@company.com" data-testid="q-email" />
                  </div>
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">Phone</label>
                    <input type="tel" value={form.phone} onChange={(e) => updateForm("phone", e.target.value)} className="qms-input" placeholder="+1 (555) 000-0000" data-testid="q-phone" />
                  </div>
                </div>
              </>
            )}

            {/* Step 1: Sector */}
            {step === 1 && (
              <>
                <h3 className="font-['Syne'] text-lg font-bold text-white">Industry Sector</h3>
                <p className="text-[#8B95A5] text-sm">Select the primary industry sector</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {SECTORS.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => updateForm("sector", s.id)}
                      className={`p-4 rounded-[3px] text-left transition-all ${
                        form.sector === s.id
                          ? "bg-[#36A9E1] bg-opacity-15 border border-[#36A9E1] text-[#38BDF8]"
                          : "bg-[#181B23] border border-[rgba(255,255,255,0.06)] text-[#8B95A5] hover:border-[#2D313D]"
                      }`}
                      data-testid={`q-sector-${s.id}`}
                    >
                      <span className="font-medium text-sm">{s.label}</span>
                    </button>
                  ))}
                </div>
                {selectedSector && (
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-2 mt-4">Applicable Standards (select all)</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedSector.standards.map((std) => (
                        <button
                          key={std}
                          onClick={() => toggleArray("current_certifications", std)}
                          className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                            form.current_certifications.includes(std)
                              ? "bg-[#E8A838] bg-opacity-20 border border-[#E8A838] text-[#FBBF24]"
                              : "bg-[#1F222D] border border-[rgba(255,255,255,0.06)] text-[#8B95A5]"
                          }`}
                          data-testid={`q-std-${std.replace(/\s+/g, "-")}`}
                        >
                          {std}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Step 2: Scope */}
            {step === 2 && (
              <>
                <h3 className="font-['Syne'] text-lg font-bold text-white">Scope of Work</h3>
                <p className="text-[#8B95A5] text-sm">Describe the scope of the consulting engagement</p>
                <textarea
                  value={form.scope}
                  onChange={(e) => updateForm("scope", e.target.value)}
                  className="qms-input h-36 resize-none"
                  placeholder="e.g., Full QMS implementation for our sterile manufacturing facility, including document control, training, validation protocols, and preparation for FDA inspection..."
                  data-testid="q-scope"
                />
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('questionnaire.timeline.label', 'Timeline Preference')}</label>
                  <div className="flex flex-wrap gap-2">
                    {["3 months", "6 months", "9 months", "12 months", "Flexible"].map((period) => (
                      <button
                        key={period}
                        onClick={() => updateForm("timeline_preference", period)}
                        className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                          form.timeline_preference === period
                            ? "bg-[#36A9E1] bg-opacity-15 border border-[#36A9E1] text-[#38BDF8]"
                            : "bg-[#1F222D] border border-[rgba(255,255,255,0.06)] text-[#8B95A5]"
                        }`}
                        data-testid={`q-timeline-${period}`}
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Objectives */}
            {step === 3 && (
              <>
                <h3 className="font-['Syne'] text-lg font-bold text-white">Project Objectives</h3>
                <p className="text-[#8B95A5] text-sm">Select all applicable objectives</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {OBJECTIVE_OPTIONS.map((obj) => (
                    <button
                      key={obj}
                      onClick={() => toggleArray("objectives", obj)}
                      className={`p-3 rounded-[3px] text-left text-sm transition-all flex items-start gap-2 ${
                        form.objectives.includes(obj)
                          ? "bg-[#4ADE80] bg-opacity-10 border border-[#4ADE80] border-opacity-40 text-[#4ADE80]"
                          : "bg-[#181B23] border border-[rgba(255,255,255,0.06)] text-[#8B95A5] hover:border-[#2D313D]"
                      }`}
                      data-testid={`q-obj-${obj.slice(0, 20).replace(/\s+/g, "-")}`}
                    >
                      {form.objectives.includes(obj) ? <CheckCircle size={16} className="flex-shrink-0 mt-0.5" /> : <div className="w-4 h-4 rounded-full border border-[#2D313D] flex-shrink-0 mt-0.5" />}
                      {obj}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Step 4: Goals */}
            {step === 4 && (
              <>
                <h3 className="font-['Syne'] text-lg font-bold text-white">Goals & Expected Outcomes</h3>
                <p className="text-[#8B95A5] text-sm">What does success look like for this project?</p>
                <textarea
                  value={form.goals}
                  onChange={(e) => updateForm("goals", e.target.value)}
                  className="qms-input h-28 resize-none"
                  placeholder="e.g., Achieve ISO 13485 certification within 9 months, pass FDA inspection with zero critical findings, reduce customer complaints by 50%..."
                  data-testid="q-goals"
                />
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Additional Notes</label>
                  <textarea
                    value={form.additional_notes}
                    onChange={(e) => updateForm("additional_notes", e.target.value)}
                    className="qms-input h-20 resize-none"
                    placeholder={t('questionnaire.otherInfo')}
                    data-testid="q-notes"
                  />
                </div>
              </>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t border-[rgba(255,255,255,0.06)]">
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="btn-ghost py-2 px-4 disabled:opacity-30"
                data-testid="q-prev-btn"
              >
                <ChevronLeft size={16} /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canNext()}
                  className="btn-primary py-2 px-4 disabled:opacity-30"
                  data-testid="q-next-btn"
                >
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={generateProposal}
                  disabled={!canNext()}
                  className="btn-primary py-2 px-5 disabled:opacity-30"
                  data-testid="q-generate-btn"
                >
                  <Sparkles size={16} /> Generate Proposal
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Summary Bar */}
        {(form.company || form.sector) && (
          <div className="mt-4 p-3 bg-[#1F222D] rounded-[3px] border border-[rgba(255,255,255,0.06)] flex flex-wrap gap-3 text-xs text-[#8B95A5]">
            {form.company && <span>Company: <span className="text-[#CBD5E1]">{form.company}</span></span>}
            {form.sector && <span>Sector: <span className="text-[#CBD5E1]">{SECTORS.find((s) => s.id === form.sector)?.label}</span></span>}
            {form.objectives.length > 0 && <span>Objectives: <span className="text-[#CBD5E1]">{form.objectives.length} selected</span></span>}
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default ClientQuestionnairePage;
